import { Controller } from '@hotwired/stimulus'
import { FormSubmission } from '@hotwired/turbo'

import {
  LoginService,
  LoginDelegate,
  LoginCredentials,
  OtpCredentials,
} from '../services/authentication/loginService'

/**
 * LoginController
 *
 * Responsible for handling the login form submission and coordinating with LoginService
 * for authentication and device key management.
 *
 * This controller:
 * - Intercepts form submissions
 * - Provides form targets for email and password
 * - Implements LoginDelegate to handle various login events
 * - Manages the UI state during the login process
 */
export default class LoginController extends Controller<HTMLFormElement> implements LoginDelegate {
  private loginService: LoginService = new LoginService(this)

  static targets = ['submitButton']

  declare submitButtonTarget: HTMLButtonElement
  declare hasSubmitButtonTarget: boolean

  connect() {
    window.sessionStorage.removeItem('deviceKey')
  }

  /**
   * Handles the form submission start event
   * Intercepts the form submission and delegates to LoginService
   *
   * Should be called on-submit-start, e.g.
   *
   * <form data-controller="login" data-action="turbo:submit-start->login#onSubmitStart">
   */
  async onSubmitStart(event: CustomEvent<{ formSubmission: FormSubmission }>) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
    const formData = stopFormSubmission(event)
    const credentials = extractCredentials(formData)

    try {
      await this.loginService.call(credentials)
    } catch (e) {
      if (this.hasSubmitButtonTarget) {
        this.submitButtonTarget.disabled = false
      }
      throw e
    }
  }

  // LoginDelegate implementation
  async onAuthenticationError(response: Response): Promise<void> {
    this.dispatch('onAuthenticationError')
  }

  async onAuthenticationComplete(email: string, password: string): Promise<void> {
    this.dispatch('onAuthenticationComplete', {
      detail: {
        email,
        password,
      },
    })
  }

  async onAuthenticationAndKeyLoadingComplete(email: string, password: string): Promise<void> {
    // No additional UI handling needed at this point
  }

  async onDeviceKeyLoadingError(): Promise<void> {
    Turbo.visit('/encryption/unlocking_error', { action: 'replace' })
  }

  async onDeviceKeyLoaded(): Promise<void> {
    // No additional UI handling needed at this point
  }
}

function getLastValue<T>(values: T[]): T | undefined {
  return values.length > 0 ? values[values.length - 1] : undefined
}

export function stopFormSubmission(event: CustomEvent<{ formSubmission: FormSubmission }>) {
  event.preventDefault()
  event.stopPropagation()

  const formSubmission = event.detail.formSubmission
  formSubmission.stop()
  // Hack: remove the submitter to prevent Turbo from de-disabling submit button
  formSubmission.submitter = undefined
  return formSubmission.body
}

export function extractCredentials(
  formData: FormData | URLSearchParams,
): LoginCredentials | OtpCredentials {
  if (formData.has('patient_user[otp_attempt]')) {
    return {
      otp_attempt: formData.get('patient_user[otp_attempt]')?.toString() || '',
      // trust_device is a checkbox, which means rails creates a hidden field with the value '0' and a checkbox with the value '1'
      // To get the value, we need to get the last value from the array
      trust_device: getLastValue(formData.getAll('patient_user[trust_device]'))?.toString() || '',
    }
  } else {
    return {
      email: formData.get('patient_user[email]')?.toString() || '',
      password: formData.get('patient_user[password]')?.toString() || '',
    }
  }
}
